import SEO from "../components/SEO";
import { motion } from "framer-motion";
import { ArrowRight, BarChart3, Users, Home, DollarSign, ClipboardCheck, FileText, CheckCircle, TrendingUp, PieChart, Building2, Heart, Menu, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

// Images
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/d088a33042372fca314e74d5a45a0c5503b4a0a66addc988688a6ed0d6fbe2bb.png";
const DATA_IMAGE = "https://images.unsplash.com/photo-1758876202980-0a28b744fb24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRhdGElMjBhbmFseXRpY3MlMjBkYXNoYm9hcmQlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcyOTc1OTA0fDA&ixlib=rb-4.1.0&q=85";
const TEAM_IMAGE = "https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwZW1wbG95ZWVzJTIwb2ZmaWNlJTIwdGVhbXdvcmslMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3Mjk3NTkwNXww&ixlib=rb-4.1.0&q=85";
const HOMEOWNER_IMAGE = "https://images.unsplash.com/photo-1758523671819-06a0f1941520?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBuZXclMjBob21lJTIwa2V5cyUyMGhhcHB5JTIwaG9tZW93bmVyfGVufDB8fHx8MTc3Mjk3NTkwNXww&ixlib=rb-4.1.0&q=85";

const RetentionAnalysisPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  const analysisIncludes = [
    {
      icon: BarChart3,
      title: "Workforce Retention Study",
      description: "CityWorth reviews workforce data to understand employee tenure and retention trends.",
      items: ["Median employee tenure", "Turnover patterns", "Estimated cost of employee turnover"]
    },
    {
      icon: Home,
      title: "Homeowner vs Renter Retention Analysis",
      description: "Using our proprietary housing analysis technology, CityWorth identifies which employees are homeowners and which are renters based on employee address data.",
      items: ["Median tenure of homeowners vs renters", "Retention differences between the two groups", "Workforce stability patterns related to housing"]
    },
    {
      icon: TrendingUp,
      title: "Employee Homeownership Opportunity",
      description: "CityWorth estimates how many employees could realistically become homeowners within the next 12 to 24 months.",
      items: ["Estimated employee income ranges", "Local housing markets where employees live", "Typical mortgage qualification standards", "Down payment requirements"]
    },
    {
      icon: PieChart,
      title: "Housing Affordability Analysis",
      description: "CityWorth evaluates housing affordability in the markets where employees live.",
      items: ["Median home prices in employee markets", "Estimated mortgage payments", "Income required to qualify for homeownership", "Affordability gaps preventing employees from buying homes"]
    },
    {
      icon: DollarSign,
      title: "Employer Contribution Impact",
      description: "The report models how employer homeownership support could improve affordability and participation.",
      items: ["$5,000 homebuying support modeling", "$10,000 homebuying support modeling", "$15,000 homebuying support modeling", "Potential participation levels in a homeownership benefit"]
    },
    {
      icon: ClipboardCheck,
      title: "Employee Sentiment Survey",
      description: "CityWorth may also conduct an employee survey to gather additional insight about homeownership interest and perceived barriers.",
      items: ["Interest in becoming a homeowner", "Obstacles preventing employees from buying homes", "Interest in employer homebuying support", "Whether homeownership would increase long term employment stability"]
    }
  ];

  const deliverables = [
    "Workforce retention study",
    "Homeowner vs renter retention comparison",
    "Housing affordability analysis",
    "Homeownership opportunity modeling",
    "Employee sentiment insights",
    "Executive report with strategy recommendations"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white"
      data-testid="retention-analysis-page"
    >
      <SEO title="Retention Analysis" description="Discover how CityWorth analyzes workforce data to show the ROI of homeownership benefits for employee retention." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    >
                      {menuItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={item.action}
                          className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-12 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Data-Driven Insights</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Employee Retention{" "}
                <span className="gradient-text">Analysis</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-6">
                A data-driven report that shows how housing stability and homeownership impact employee retention inside your company and helps determine the ROI of implementing a homeownership program.
              </p>

              <p className="text-lg text-slate-500 mb-8">
                CityWorth combines workforce data, housing data, and employee sentiment to uncover patterns that help employers understand how homeownership can strengthen workforce stability and measure the potential return on investment.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/get-started">
                  <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                    Request Your Analysis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Data analytics dashboard" 
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border border-slate-100"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900">Data-Driven</div>
                    <div className="text-sm text-slate-500">Retention Insights</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              How the{" "}
              <span className="gradient-text">Analysis Works</span>
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img 
                src={DATA_IMAGE} 
                alt="Colleagues discussing data" 
                className="w-full h-80 object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg text-slate-600 mb-6">
                Using our proprietary software, CityWorth analyzes employee address data to determine which employees are homeowners and which are renters. This allows us to identify housing stability patterns across the workforce.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                We then combine this housing data with workforce information such as employee tenure and employment history to measure how retention differs between homeowners and renters.
              </p>

              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">This analysis helps employers understand:</h3>
                <ul className="space-y-3">
                  {[
                    "How much longer homeowners stay with the company compared to renters",
                    "Retention patterns across the workforce",
                    "The stability impact of homeownership",
                    "The potential ROI of implementing a homeownership program",
                    "Opportunities to improve retention through housing stability"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Analysis Includes Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              What the Analysis{" "}
              <span className="gradient-text">Includes</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {analysisIncludes.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-blue-500 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 mb-4">{item.description}</p>
                <ul className="space-y-2">
                  {item.items.map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                What Employers{" "}
                <span className="gradient-text">Receive</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Companies receive a comprehensive Employee Retention Analysis including:
              </p>
              <div className="grid grid-cols-1 gap-3">
                {deliverables.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-slate-200"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img 
                src={TEAM_IMAGE} 
                alt="Diverse team celebrating success" 
                className="w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-blue-800" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-yellow-400/20 via-transparent to-transparent" />
        
        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Understand How Homeownership Impacts Your Retention
            </h2>
            <p className="text-xl text-white/90 mb-10">
              CityWorth's Employee Retention Analysis helps companies understand how much longer homeowners stay with the company compared to renters and identifies opportunities to improve retention through housing stability.
            </p>
            <Link to="/get-started">
              <Button 
                size="lg" 
                className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-10 py-7 text-xl font-bold shadow-2xl shadow-yellow-400/30 hover:shadow-yellow-400/50 transition-all hover:scale-105"
              >
                Request Your Analysis
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/retention-analysis" className="text-slate-600 hover:text-slate-900">Retention Analysis</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
            <Link to="/careers" className="text-slate-600 hover:text-slate-900">Careers</Link>
            <Link to="/get-started" className="text-slate-600 hover:text-slate-900">Contact</Link>
          </div>
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </motion.div>
  );
};

export default RetentionAnalysisPage;
